import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Pill, Package, PackagePlus, RotateCcw, AlertTriangle, Loader2, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  pharmacyApi,
  type ExpiryRiskBucketKey,
  type StockRiskRow,
  type StockRiskUrgency,
} from "@/api/pharmacy";
import { format, parseISO } from "date-fns";

function urgencyBadge(urgency: StockRiskUrgency) {
  if (urgency === "CRITICAL") {
    return (
      <Badge variant="outline" className="text-red-800 border-red-600 bg-red-50">
        Critical
      </Badge>
    );
  }
  if (urgency === "HIGH") {
    return (
      <Badge variant="outline" className="text-amber-900 border-amber-600 bg-amber-50">
        High
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-slate-700 border-slate-400 bg-slate-50">
      Watch
    </Badge>
  );
}

function formatEmptyWhen(row: StockRiskRow) {
  if (row.onHand <= 0) return "Already over";
  if (row.daysToEmpty == null) return "—";
  if (row.daysToEmpty <= 0) return "Already over";
  const when = row.estimatedEmptyDate
    ? format(parseISO(row.estimatedEmptyDate), "dd MMM yyyy")
    : null;
  if (row.daysToEmpty < 1) return when ? `Today (~${when})` : "Today";
  if (row.daysToEmpty <= 1.5) return when ? `~1 day (${when})` : "~1 day";
  return when
    ? `~${Math.ceil(row.daysToEmpty)} days (${when})`
    : `~${Math.ceil(row.daysToEmpty)} days`;
}

function expiryDaysBadge(days: number) {
  if (days <= 30) {
    return (
      <Badge variant="outline" className="text-red-800 border-red-600 bg-red-50">
        {days}d
      </Badge>
    );
  }
  if (days <= 60) {
    return (
      <Badge variant="outline" className="text-amber-900 border-amber-600 bg-amber-50">
        {days}d
      </Badge>
    );
  }
  if (days <= 90) {
    return (
      <Badge variant="outline" className="text-yellow-900 border-yellow-600 bg-yellow-50">
        {days}d
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-slate-700 border-slate-400 bg-slate-50">
      {days}d
    </Badge>
  );
}

const EXPIRY_TAB_ORDER: ExpiryRiskBucketKey[] = ["0_30", "31_60", "61_90", "91_180"];

export default function PharmacistDashboard() {
  const [expiryTab, setExpiryTab] = useState<ExpiryRiskBucketKey>("0_30");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["pharmacy-stock-risk"],
    queryFn: async () => {
      const res = await pharmacyApi.getStockRisk({ lookbackDays: 30, maxDaysToEmpty: 14, limit: 50 });
      return res.data?.data ?? null;
    },
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const {
    data: expiryData,
    isLoading: expiryLoading,
    isError: expiryError,
    error: expiryErr,
  } = useQuery({
    queryKey: ["pharmacy-expiry-risk"],
    queryFn: async () => {
      const res = await pharmacyApi.getExpiryRisk({ horizonDays: 180, limit: 300 });
      return res.data?.data ?? null;
    },
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const items = data?.items ?? [];
  const expiryBuckets = expiryData?.buckets ?? [];
  const expiryItemsByBucket = useMemo(() => {
    const map: Record<ExpiryRiskBucketKey, NonNullable<typeof expiryData>["items"]> = {
      "0_30": [],
      "31_60": [],
      "61_90": [],
      "91_180": [],
    };
    for (const row of expiryData?.items ?? []) {
      map[row.bucket].push(row);
    }
    return map;
  }, [expiryData]);

  const totalExpiring = expiryData?.items.length ?? 0;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Pharmacy</h1>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Medicines about to finish
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Based on MAIN stock and last {data?.lookbackDays ?? 30} days of issues (net of returns).
            Order proactively when days-to-empty is ≤{data?.maxDaysToEmpty ?? 14}, or on-hand is at/below
            reorder level.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading stock risk…
            </div>
          ) : isError ? (
            <p className="text-sm text-red-700 py-4">
              {(error as Error)?.message || "Could not load stock risk"}
            </p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              No medicines are projected to finish soon. Keep issuing as usual — this list updates from
              recent sales velocity.
            </p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Urgency</TableHead>
                    <TableHead>Medicine</TableHead>
                    <TableHead className="text-right">On hand</TableHead>
                    <TableHead className="text-right">Avg / day</TableHead>
                    <TableHead>Over by</TableHead>
                    <TableHead>Why</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row) => (
                    <TableRow key={row.drugId}>
                      <TableCell>{urgencyBadge(row.urgency)}</TableCell>
                      <TableCell>
                        <div className="font-medium">{row.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {[row.strength, row.form, row.unit].filter(Boolean).join(" · ")}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.onHand} {row.unit}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.avgDailyUsage > 0 ? row.avgDailyUsage.toFixed(1) : "—"}
                      </TableCell>
                      <TableCell className="font-medium whitespace-nowrap">
                        {formatEmptyWhen(row)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[16rem]">
                        {row.reason}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild size="sm" variant="outline">
                          <Link to="/pharmacist/stock-in">Stock in</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/pharmacist/stock">View all stock</Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/pharmacist/stock-in">Record stock-in</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-5 w-5 text-rose-600" />
            Medicines nearing expiry
            {totalExpiring > 0 ? (
              <Badge variant="secondary" className="ml-1">
                {totalExpiring}
              </Badge>
            ) : null}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Batches with stock on hand expiring in the next 30, 60, 90, or 180 days. Prioritise FEFO
            issue, return to vendor, or mark for disposal before they expire.
          </p>
        </CardHeader>
        <CardContent>
          {expiryLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading expiry risk…
            </div>
          ) : expiryError ? (
            <p className="text-sm text-red-700 py-4">
              {(expiryErr as Error)?.message || "Could not load expiry risk"}
            </p>
          ) : totalExpiring === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              No batches with stock are expiring within the next 180 days.
            </p>
          ) : (
            <Tabs
              value={expiryTab}
              onValueChange={(v) => setExpiryTab(v as ExpiryRiskBucketKey)}
            >
              <TabsList className="flex flex-wrap h-auto gap-1">
                {EXPIRY_TAB_ORDER.map((key) => {
                  const bucket = expiryBuckets.find((b) => b.key === key);
                  return (
                    <TabsTrigger key={key} value={key} className="gap-1.5">
                      {bucket?.label ?? key}
                      <Badge variant="secondary" className="ml-0.5">
                        {bucket?.batchCount ?? 0}
                      </Badge>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              {EXPIRY_TAB_ORDER.map((key) => {
                const rows = expiryItemsByBucket[key];
                return (
                  <TabsContent key={key} value={key} className="mt-4">
                    {rows.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-2">
                        No batches in this window.
                      </p>
                    ) : (
                      <div className="rounded-md border overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Days left</TableHead>
                              <TableHead>Medicine</TableHead>
                              <TableHead>Batch</TableHead>
                              <TableHead>Location</TableHead>
                              <TableHead className="text-right">Qty</TableHead>
                              <TableHead>Expiry</TableHead>
                              <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rows.map((row) => (
                              <TableRow key={row.batchId}>
                                <TableCell>{expiryDaysBadge(row.daysUntilExpiry)}</TableCell>
                                <TableCell>
                                  <div className="font-medium">{row.name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {[row.strength, row.form, row.unit]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </div>
                                </TableCell>
                                <TableCell className="font-mono text-sm">{row.batchNo}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="font-normal">
                                    {row.location}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {row.quantity} {row.unit}
                                </TableCell>
                                <TableCell className="whitespace-nowrap">
                                  {format(parseISO(row.expiryDate), "dd MMM yyyy")}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button asChild size="sm" variant="outline">
                                    <Link to="/pharmacist/stock">View stock</Link>
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Pill className="h-5 w-5" />
              Prescription Queue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              View and dispense OPD and IPD prescriptions. Issue creates bill (OPD) or accrues to admission (IPD).
            </p>
            <Button asChild>
              <Link to="/pharmacist/queue">Open queue</Link>
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Stock & Batches
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Check available stock and batches by drug (FIFO order).
            </p>
            <Button asChild variant="outline">
              <Link to="/pharmacist/stock">View stock</Link>
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PackagePlus className="h-5 w-5" />
              Stock In / Purchase
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Record incoming stock for medicines (purchases) and update batch quantities.
            </p>
            <Button asChild variant="outline">
              <Link to="/pharmacist/stock-in">Record stock-in</Link>
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Returns
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Record returns and reverse stock to batch.
            </p>
            <Button asChild variant="outline">
              <Link to="/pharmacist/returns">Record return</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
